import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Container, Header, Segment, Divider, Icon, List, Grid, Card, Step, Progress } from 'semantic-ui-react';
import './Playbook.css';

class OnboardingTraining extends Component {
    render() {
        return (
            <Container className="playbook-section-container">
                <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                    <i aria-hidden="true" className="icon">
                        <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                    </i>
                    Onboarding/Training
                </h1>
                <div className="ui divider"></div>

                <Segment className="playbook-content-segment">
                    <Header as="h2" color="green">Training Process – Explained</Header>
                    
                    <Header as="h3">Introduction</Header>
                    <p>
                        The Multiply Sales Management Framework is a set of processes, tools, and templates that guides a company in the areas of:
                    </p>
                    <List bulleted>
                        <List.Item>Message creation</List.Item>
                        <List.Item>Preparation of the salesperson/team</List.Item>
                        <List.Item>Activity management/meeting control</List.Item>
                        <List.Item>Proposing on opportunities</List.Item>
                        <List.Item>Ongoing sales leadership and message maintenance</List.Item>
                    </List>

                    <p>
                        The framework simplifies the process. As a result, salespeople are able to approach prospects knowing what to say, are able to 
                        control meetings, and are able to predict responses from clients. This becomes a very repeatable process.
                    </p>

                    <Segment color="yellow">
                        <Header as="h4">Prerequisites</Header>
                        <p>In this module, we are working through how to train a salesperson into your message. There are some basic premises that you need to have completed in order to begin onboarding. Make sure that you have:</p>
                        <List bulleted>
                            <List.Item>Created your message and documented it inside the Multiply application.</List.Item>
                            <List.Item>Built your pitch deck, even if most of your sales do not come from formal presentation-style pitches. We will explain why later.</List.Item>
                            <List.Item>Added your sales team members to the Multiply application based on their role. A Multiply consultant can help you with this.</List.Item>
                        </List>
                    </Segment>

                    <Divider />

                    <Header as="h3" color="green">We are working toward a state of comfort</Header>
                    <p>
                        Let's get something out of the way: We are fully aware that this process can and will be uncomfortable. That's the point. However, 
                        once your sales team members go through it, they will possess a knowledge base and the confidence to carry your company's messaging 
                        in any customer setting they find themselves in—and they will be able to do it with ease.
                    </p>

                    <p>The pressure is twofold. There is pressure on:</p>
                    <List numbered>
                        <List.Item>The leadership team of your company</List.Item>
                        <List.Item>The sales team members</List.Item>
                    </List>

                    <Grid stackable columns={2}>
                        <Grid.Column>
                            <Card>
                                <Card.Content>
                                    <Card.Header color="red">Sales Leadership "Discomfort"</Card.Header>
                                    <Card.Description>
                                        <p>
                                            This process puts pressure on the leaders of companies, whether they are founders of smaller companies 
                                            looking to define a message, create visibility into the sales management process, and build out a team 
                                            or they are the VP of Sales or Chief Revenue Officers looking to start a new process with an established team.
                                        </p>
                                        <p>
                                            If we are to tell someone how to do something, we must first define what to tell them. That is why we have 
                                            come up with this methodology.
                                        </p>
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card>
                                <Card.Content>
                                    <Card.Header color="blue">Sales Team "Discomfort"</Card.Header>
                                    <Card.Description>
                                        <p>
                                            This process is built on presenting in front of your peers and bosses in a variety of settings. That is VERY 
                                            uncomfortable, especially early in the process.
                                        </p>
                                        <p>
                                            Here is the good news: The internal audience is BY FAR the toughest audience. There are a couple of reasons 
                                            for this. Our team members and bosses know the "right" answers. When we present to our peers, they know what 
                                            we are supposed to be saying.
                                        </p>
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <Divider />

                    <Header as="h3" color="green">The Process</Header>
                    <p>
                        You've seen the approach and defined your message and pitch deck via the app.askmultiply.com and the Messaging Module content. 
                        Now it is time to learn to apply it. As you have seen, the Multiply methodology is built upon creating a repeatable pattern of a 
                        conversation and repeating it in whatever setting you find yourself based on the level of detail needed given the setting.
                    </p>

                    <p>If a sales team member can deliver the message in the following settings, then he or she will be very well equipped to "sell". The settings are:</p>
                    
                    <Step.Group stackable="tablet" widths={4}>
                        <Step>
                            <Icon name="users" />
                            <Step.Content>
                                <Step.Title>Boardroom</Step.Title>
                                <Step.Description>Formal presentation using pitch deck</Step.Description>
                            </Step.Content>
                        </Step>
                        
                        <Step>
                            <Icon name="coffee" />
                            <Step.Content>
                                <Step.Title>Casual</Step.Title>
                                <Step.Description>Over coffee, drinks, lunch, etc.</Step.Description>
                            </Step.Content>
                        </Step>
                        
                        <Step>
                            <Icon name="phone" />
                            <Step.Content>
                                <Step.Title>Phone Call</Step.Title>
                                <Step.Description>The Elevator Pitch scenario in the app</Step.Description>
                            </Step.Content>
                        </Step>
                        
                        <Step>
                            <Icon name="video camera" />
                            <Step.Content>
                                <Step.Title>Video Conference</Step.Title>
                                <Step.Description>Online meetings and demos</Step.Description>
                            </Step.Content>
                        </Step>
                    </Step.Group>

                    <Divider />

                    <Header as="h3" color="green">Timeline</Header>
                    <p>
                        The preparation process should take 6-8 weeks, assuming the message and pitch deck are already built in the Multiply application. 
                        This process is the same whether you are onboarding a new hire into your organization, promoting a team member to a sales position 
                        from an operations position, or working with experienced sales team members to build consistency.
                    </p>

                    <Progress percent={100} color="green" size="large">8-Week Training Program</Progress>

                    <p>During the 8 weeks, the team members will:</p>
                    <List bulleted>
                        <List.Item>Conduct up to 16 review sessions of each type of encounter (boardroom presentation, casual, phone, and video)</List.Item>
                        <List.Item>Document feedback for the reviews</List.Item>
                        <List.Item>Begin applying the messaging in real time in front of real prospects (or centers of influence)</List.Item>
                    </List>

                    <Divider />

                    <Header as="h3" color="green">Activities</Header>

                    <Step.Group vertical>
                        <Step>
                            <Icon name="play" />
                            <Step.Content>
                                <Step.Title>Step 1: Begin learning the content (and don't stop…)</Step.Title>
                                <Step.Description>
                                    The first step is to gain access to the Multiply application. It is important for the sales leadership to explain 
                                    the concepts behind the messaging. The content of the application and message is meant to represent how the sales 
                                    team talks about the company. It is NOT meant to be a script.
                                </Step.Description>
                            </Step.Content>
                        </Step>

                        <Step>
                            <Icon name="eye" />
                            <Step.Content>
                                <Step.Title>Step 2: The reviews</Step.Title>
                                <Step.Description>
                                    We suggest 16 review sessions over the 8-week process. That sounds like a lot, but keep in mind that most of 
                                    these sessions will be less than an hour. Presentations will be longer meetings with other sales team members 
                                    involved, but phone calls will likely be short one-on-one conversations.
                                </Step.Description>
                            </Step.Content>
                        </Step>
                    </Step.Group>

                    <Header as="h4">Week 1: The First Review</Header>
                    <List bulleted>
                        <List.Item>Explain the review process and the point of it, as well as the overall process of building a consistent message</List.Item>
                        <List.Item>Sales Leadership (or a Multiply consultant) presents the story as a model</List.Item>
                        <List.Item><strong>Goal:</strong> Sales team understands the concepts and application of the messaging concepts</List.Item>
                        <List.Item><strong>Goal:</strong> Sales team members begin learning and practicing the messaging on their own</List.Item>
                    </List>

                    <Segment color="teal">
                        <Header as="h4">Multiply Tip</Header>
                        <p>
                            Make it culturally okay to admit when you messed up, left something out, etc. That's how we get better. As long as this 
                            process occurs for the right reasons (making the employee better and helping the company land deals), and EVERYONE is 
                            subjected to it, then it can work.
                        </p>
                    </Segment>

                    <Header as="h4">Week 2-5: Initial Reviews</Header>
                    <List bulleted>
                        <List.Item>Conduct at least 8 reviews with team members.</List.Item>
                        <List.Item><strong>Goal:</strong> Begin building comfort in constructing the messaging and controlling a meeting.</List.Item>
                    </List>

                    <p>
                        This part of the process is where it is very uncomfortable. This is new, and most team members will struggle. That is absolutely 
                        to be expected. These reviews are where the sales leader sets meetings (and even surprise calls) to have the sales team members 
                        present the company story in the four (4) settings we discussed above.
                    </p>

                    <Segment color="orange">
                        <Header as="h4">Multiply Tip: The Ambush Call</Header>
                        <p>
                            Call your sales team members out of the blue and act as if you are a prospect who found their number and is interested 
                            in hearing more about the company or a product. This will catch the salesperson off guard, and they will likely struggle 
                            the first time. This encounter rarely happens in real life, but it CAN happen and is actually the best case scenario for 
                            a salesperson.
                        </p>
                    </Segment>

                    <Header as="h4">Week 5-8: The Final Preparation</Header>
                    <List bulleted>
                        <List.Item>Simulate 8 real-world encounters as closely as possible, including distractions and strong objections.</List.Item>
                        <List.Item>Demonstrate capabilities to talk through offerings.</List.Item>
                        <List.Item><strong>Goal:</strong> Know how to apply and have the capabilities to apply the messaging in the four (4) settings.</List.Item>
                        <List.Item><strong>Goal:</strong> Know how to execute and control a meeting starting at introductions, messaging, and asks, and then ending with follow-ups.</List.Item>
                    </List>

                    <p>During these final reviews, the sales leader and review participants should:</p>
                    <List bulleted>
                        <List.Item>Issue objections using the objections documented in the offerings sections of the Multiply application.</List.Item>
                        <List.Item>Create distractions like picking up your phone to check messages. If in a casual setting, stand up as if to meet a colleague while the salesperson is still talking or mimic a waiter coming by to take an order.</List.Item>
                        <List.Item>Try to take the control of the conversation away from the salesperson.</List.Item>
                    </List>

                    <Divider />

                    <Header as="h3" color="green">Red, Yellow, Green ?</Header>
                    <p>
                        In the Multiply application, you will see the capabilities to rank each review as red, yellow, or green. We are constantly asked 
                        what determines the colors. The answer is that we know what bad looks like and we know what really good looks like—and then there 
                        is something in the middle.
                    </p>

                    <Grid stackable columns={3}>
                        <Grid.Column>
                            <Card color="red">
                                <Card.Content>
                                    <Card.Header>Red</Card.Header>
                                    <Card.Description>
                                        Someone who is uncomfortable, lacks confidence, lacks knowledge, and is easily thrown off by the simplest of 
                                        objections or distractions in 2 or more of the 4 scenarios. This person will likely sound like they are on a script.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="yellow">
                                <Card.Content>
                                    <Card.Header>Yellow</Card.Header>
                                    <Card.Description>
                                        Someone who understands the knowledge of the companies and offerings but doesn't sound like he/she is on a script. 
                                        This person may not be able to handle all objections and/or may display less comfort in one or two of the review settings.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="green">
                                <Card.Content>
                                    <Card.Header>Green</Card.Header>
                                    <Card.Description>
                                        A confident (not arrogant) seller who demonstrates that he/she can control a meeting as the methodology suggests 
                                        and also possesses conversational knowledge of the company and offerings.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <Divider />

                    <Header as="h3" color="green">It is NOT a Script</Header>
                    <p>
                        We all know when someone we are talking to is on a script. If you are like me, you might even try to knock them off the script 
                        to see if they can get back on it. Many others in the market talk about a "sales script". WE DO NOT. Scripts can be very important 
                        and useful in other settings such as call centers where a very defined set of information needs to be conveyed.
                    </p>

                    <p>We are talking about becoming relevant in a conversation. When we are on a script, there are huge problems with our presentation and our limitations become obvious:</p>
                    <List bulleted>
                        <List.Item>Not appearing credible/knowledgeable</List.Item>
                        <List.Item>Unable to deal with distraction</List.Item>
                        <List.Item>Unable to "read the room"</List.Item>
                        <List.Item>Unable to handle objections</List.Item>
                    </List>

                    <Segment color="blue">
                        <Header as="h4">Key Principle</Header>
                        <p>
                            A good salesperson has a knowledge base of information to rely on, is practiced and rehearsed at conveying the points, and 
                            focuses on the message he/she wants to deliver and not the specific words or order in which the topics are conveyed.
                        </p>
                    </Segment>

                    <Segment color="green">
                        <Header as="h4">Multiply Tip: Practice Audiences</Header>
                        <p>
                            Ok, so this might be a little embarrassing, but we have found dogs or other pets make great practice audiences. When you 
                            (or we) are practicing at home, call the dogs in. There is a point to this. Dogs look at you, they tilt their head, they 
                            seemingly make faces, and then they create all sorts of distractions. You know, like humans 😊.
                        </p>
                        <p>
                            If pets do not work for you, then practice the message out loud while driving your vehicle. You will obviously have to 
                            process "distractions" while saying the message which can simulate the same types of thought processes that occur when you 
                            get interrupted or thrown off course in a sales meeting.
                        </p>
                    </Segment>

                    <Divider />

                    <Header as="h3" color="green">Conclusion</Header>
                    <p>
                        We know this is a heavy process. We know that every team is not going to go through every step. That is fine as long as the 
                        leadership is managing the sales team consistently. If the leadership chooses to conduct fewer activities than prescribed, then 
                        make sure it is because you have confidence in your team and NOT because you fear some sort of discomfort with your team. 
                        Trust but verify!
                    </p>

                    <p>
                        Of course we recommend all of it. We would not have built this module if we didn't know it worked and have the case studies to 
                        prove it. However, even if you don't do it all but maintain the intent and discipline of keeping up the message and making your 
                        team present it, then your team WILL be better for it and your company will produce better sales results.
                    </p>
                </Segment>
            </Container>
        );
    }
}

export default observer(OnboardingTraining);